/**
 * 扫码落地 → 邀请码捕获与回填（分佣推广闭环）。
 *
 * <p>链路：推荐官在「我的分佣」页下载二维码海报 → 新用户扫码打开
 * `/?invite=XXXXXX` → 路由守卫在**重定向之前**把邀请码落进
 * {@link PENDING_INVITE_CODE_KEY}（游客会被拦到精选页，query 会丢）→
 * 登录/注册弹窗打开时（{@code LoginDialog.vue}）读取并切到注册表单、
 * 回填邀请码字段，用后即焚（removeItem）。</p>
 *
 * <p>同一条守卫里还会**上报一次落地**（{@code POST /commission/scan}，公开接口），
 * 这是推荐官漏斗第一环「扫码 / 点击」的唯一数据来源 —— 埋点必须发生在注册之前，
 * 因为扫码的人此刻还只是个游客。</p>
 *
 * <p>⚠️ 已登录用户忽略（不消费 key）：归属绑定只发生在注册时，登录态没有意义。
 * 但**落地仍会上报** —— 登录用户点开海报同样是一次点击。</p>
 */

import { reportInviteScan } from "@/@api/commissionApi";

export const PENDING_INVITE_CODE_KEY = "pending_invite_code";

/** 会话内「已上报过落地」的标记前缀（同一个码只上报一次，防刷新灌水） */
const SCAN_REPORTED_PREFIX = "invite_scan_reported:";

/** 邀请码字符集与长度口径与后端一致：6 位、大写字母数字（这里放宽到 4–12 位容错） */
const INVITE_CODE_RE = /^[A-Za-z0-9]{4,12}$/;

/**
 * 路由守卫调用：把 URL 上的邀请码存起来，并**顺带上报一次落地**（漏斗第一环「扫码 / 点击」）。
 *
 * @param invite `to.query.invite`（可能是数组 / undefined）
 * @returns 是否真的捕获到（守卫据此决定「是否自动弹出注册弹窗」）
 */
export const capturePendingInviteCode = (invite: unknown): boolean => {
  const code = Array.isArray(invite) ? invite[0] : invite;
  if (typeof code !== "string") return false;
  const trimmed = code.trim().toUpperCase();
  if (!INVITE_CODE_RE.test(trimmed)) return false;
  // 后扫覆盖先扫（用户可能先后点开两个不同人的海报）
  sessionStorage.setItem(PENDING_INVITE_CODE_KEY, trimmed);
  reportScanOnce(trimmed);
  return true;
};

/**
 * 落地上报（fire-and-forget）。
 *
 * <p>同一浏览器会话内对同一个码只报一次：守卫可能因为路由重定向被调用两次，
 * 用户也可能刷新，而「扫码 / 点击」要统计的是**有多少人扫了**，不是打开了多少次。</p>
 *
 * <p>⚠️ 全部异常都被吞掉：这是旁路埋点，绝不能因为上报失败（无网络 / 隐私模式
 * 下 sessionStorage 抛异常 / 后端不可达）影响落地页 —— 用户此刻还没注册，
 * 任何多余的错误提示都会直接劝退。</p>
 */
const reportScanOnce = (code: string): void => {
  const key = `${SCAN_REPORTED_PREFIX}${code}`;
  try {
    if (sessionStorage.getItem(key)) return;
    // 先落标记再发请求：避免同一 tick 内被调用两次时重复上报
    sessionStorage.setItem(key, "1");
  } catch {
    return;
  }
  void reportInviteScan(code).catch(() => {
    /* 静默：埋点失败不影响任何用户可见行为 */
  });
};

/**
 * 登录/注册弹窗调用：取出并消费暂存的邀请码。
 *
 * @returns 6 位邀请码；没有暂存 / 已登录时返回 null
 */
export const consumePendingInviteCode = (): string | null => {
  const code = sessionStorage.getItem(PENDING_INVITE_CODE_KEY);
  if (!code) return null;
  sessionStorage.removeItem(PENDING_INVITE_CODE_KEY);
  return INVITE_CODE_RE.test(code) ? code : null;
};
